import {
  ChildPart,
  LitElement,
  render as litRender,
  RenderOptions,
  TemplateResult,
} from 'lit';
import { RefOrCallback } from 'lit/directives/ref.js';
import { ObservableObject } from './shared-state/index.js';

export class ViewContext {
  count = 0;
  valueMap: WeakMap<any, string> = new WeakMap();
  keyMap: Map<string, any> = new Map();
}

export interface ViewAnimation {
  keyframes: Keyframe[] | PropertyIndexedKeyframes | null;
  options?: number | KeyframeAnimationOptions | undefined;
}

export type EventHandler<T extends Event> = (_: T) => void;

export abstract class View {
  protected attrs: { [key: string]: string | boolean } = {};
  protected styles: { [key: string]: string } = {};
  protected animations: ViewAnimation[] = [];
  protected events: { [key: string]: EventHandler<any> } = {};
  protected elementRef?: RefOrCallback = undefined;

  static contextStore: Map<string, ViewContext> = new Map();

  abstract get body(): TemplateResult;

  static root(element: ViewElement): Element {
    return element.view.viewRoot(element);
  }

  // eslint-disable-next-line class-methods-use-this
  viewRoot(element: Element): Element {
    return element;
  }

  withAnimations(animations: ViewAnimation[]) {
    this.animations = animations;
    return this;
  }

  slot(name: string): this {
    if (name) {
      this.attrs.slot = name;
    }
    return this;
  }

  color(color: string) {
    this.style('color', color);
    return this;
  }

  style(nameOrStyles: string | { [key: string]: string }, value?: string) {
    if (typeof nameOrStyles === 'string') {
      if (value) {
        this.styles[nameOrStyles] = value;
      }
    } else {
      this.styles = {
        ...this.styles,
        ...nameOrStyles,
      };
    }

    return this;
  }

  ref(elRef: RefOrCallback) {
    this.elementRef = elRef;
    return this;
  }

  attr(
    nameOrAttrs: string | { [key: string]: string | boolean },
    value?: string | boolean
  ) {
    if (typeof nameOrAttrs === 'string') {
      if (typeof value === 'boolean') {
        this.attrs[`?${nameOrAttrs}`] = value;
        return this;
      }
      this.attrs[nameOrAttrs] = value ?? '';
      return this;
    }
    this.attrs = {
      ...this.attrs,
      ...nameOrAttrs,
    };
    return this;
  }

  className(...name: string[]) {
    return this.attr('class', name.join(' '));
  }

  context(token: string, value: any) {
    if (!View.contextStore.has(token)) {
      View.contextStore.set(token, new ViewContext());
    }
    const viewContext = View.contextStore.get(token)!;
    if (!viewContext.valueMap.has(value)) {
      const key = `${token}-${viewContext.count}`;
      viewContext.valueMap.set(value, key);
      viewContext.keyMap.set(key, value);
      viewContext.count += 1;
    }
    const key = viewContext.valueMap.get(value);
    this.style(token, key);
    return this;
  }

  environmentObject<T extends ObservableObject>(obj: T) {
    this.context(obj.key(), obj);
    return this;
  }

  protected addKeyboardEvent(
    eventName: string,
    key: string,
    handler: EventHandler<KeyboardEvent>
  ) {
    function eventHandler(e: KeyboardEvent) {
      if (e.key === key) {
        handler(e);
      }
    }

    this.events[eventName] = eventHandler;
    return this;
  }

  onKeydown(key: string, handler: EventHandler<KeyboardEvent>) {
    return this.addKeyboardEvent('onkeydown', key, handler);
  }

  onKeypress(key: string, handler: EventHandler<KeyboardEvent>) {
    return this.addKeyboardEvent('onkeypress', key, handler);
  }

  onKeyup(key: string, handler: EventHandler<KeyboardEvent>) {
    return this.addKeyboardEvent('onkeyup', key, handler);
  }

  onClick(handler: (_: MouseEvent) => void) {
    this.events.onclick = handler;
    return this;
  }

  onDblclick(handler: EventHandler<MouseEvent>) {
    this.events.ondblclick = handler;
    return this;
  }

  onMouseup(handler: EventHandler<MouseEvent>) {
    this.events.onmouseup = handler;
    return this;
  }

  onMousedown(handler: EventHandler<MouseEvent>) {
    this.events.onmousedown = handler;
    return this;
  }

  on<T extends Event>(eventName: string, handler: EventHandler<T>) {
    this.events[`on${eventName}`] = handler;
    return this;
  }
}

export function render(
  result: unknown,
  container: HTMLElement | DocumentFragment,
  options?: Partial<RenderOptions>
): ChildPart {
  if (result instanceof View) {
    return litRender(result.body, container, options);
  }
  return litRender(result, container, options);
}

export type ViewElement<T extends View = View> = LitElement & { view: T };

class TemplateView extends View {
  constructor(
    private template: (opts: {
      attrs: { [key: string]: string | boolean };
      styles: { [key: string]: string };
      animations: ViewAnimation[];
      events: { [key: string]: EventHandler<any> };
    }) => TemplateResult
  ) {
    super();
  }

  get body() {
    return this.template({
      attrs: this.attrs,
      styles: this.styles,
      animations: this.animations,
      events: this.events,
    });
  }
}

export function createView(
  template:
    | ((opts: {
        attrs: { [key: string]: string | boolean };
        styles: { [key: string]: string };
        animations: ViewAnimation[];
        events: { [key: string]: EventHandler<any> };
      }) => TemplateResult)
    | TemplateResult
): View {
  if (typeof template === 'function') {
    return new TemplateView(template);
  }
  return new TemplateView(() => template);
}

export type Clazz<T> = new (...args: any[]) => T;
